import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  Timestamp,
  onSnapshot
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';

export interface Todo {
  id: string;
  title: string;
  completed: boolean;
  date: Date;
  createdAt: Date;
  updatedAt: Date;
  userId: string;
  eventId?: string;
  eventTitle?: string;
}

const normalizeDate = (date: Date) => {
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  return normalized;
};

const sortTodos = (items: Todo[]) => {
  return [...items].sort((a, b) => a.date.getTime() - b.date.getTime());
};

export type TodoFilter = 'today' | 'week' | 'month' | 'all';

interface UseTodoListReturn {
  todos: Todo[];
  todosForDate: (filter?: TodoFilter, includeCompleted?: boolean) => Todo[];
  completedTodos: Todo[];
  overdueTodos: Todo[];
  loading: boolean;
  error: string | null;
  addTodo: (title: string, date: Date) => Promise<void>;
  toggleTodo: (todoId: string, completed: boolean) => Promise<void>;
  deleteTodo: (todoId: string) => Promise<void>;
  updateTodo: (todoId: string, title: string, date: Date) => Promise<void>;
  refreshTodos: () => Promise<void>;
}

export const useTodoList = (): UseTodoListReturn => {
  const { user } = useAuth();
  const [todos, setTodos] = useState<Todo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filtrar tarefas por data
  const todosForDate = useCallback((filter: TodoFilter = 'today', includeCompleted: boolean = false): Todo[] => {
    const today = normalizeDate(new Date());

    return todos.filter(todo => {
      if (!includeCompleted && todo.completed) return false;

      const todoDate = normalizeDate(todo.date);

      switch (filter) {
        case 'today':
          return todoDate.getTime() === today.getTime();

        case 'week': {
          const weekStart = new Date(today);
          weekStart.setDate(today.getDate() - today.getDay());
          const weekEnd = new Date(weekStart);
          weekEnd.setDate(weekStart.getDate() + 6);

          return todoDate >= weekStart && todoDate <= weekEnd;
        }

        case 'month': {
          const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
          const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);

          return todoDate >= monthStart && todoDate <= monthEnd;
        }

        case 'all':
          return true;

        default:
          return true;
      }
    });
  }, [todos]);

  // Obter tarefas concluídas
  const completedTodos = todos
    .filter(todo => todo.completed)
    .sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime());

  // Obter tarefas atrasadas (não concluídas e data anterior a hoje)
  const overdueTodos = todos.filter(todo => {
    if (todo.completed) return false;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const todoDate = new Date(todo.date);
    todoDate.setHours(0, 0, 0, 0);
    
    return todoDate < today;
  });

  // Buscar tarefas do usuário (sem listener)
  const fetchTodos = async () => {
    if (!user?.uid) {
      setTodos([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // CORRIGIDO: Buscar apenas do usuário atual
      const q = query(
        collection(db, 'todos'),
        where('userId', '==', user.uid),
        orderBy('date', 'asc')
      );

      const snapshot = await getDocs(q);
      const todosData: Todo[] = [];
      
      snapshot.forEach((docSnapshot) => {
        const data = docSnapshot.data();

        // VERIFICAÇÃO EXTRA: Garantir que é do usuário
        if (data.userId !== user.uid) {
          console.warn('Todo de outro usuário encontrado:', docSnapshot.id);
          return;
        }

        const todoDate = data.date?.toDate ? data.date.toDate() : new Date();

        todosData.push({
          id: docSnapshot.id,
          title: data.title || '',
          completed: data.completed || false,
          date: normalizeDate(todoDate),
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(),
          updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date(),
          userId: data.userId,
          eventId: data.eventId,
          eventTitle: data.eventTitle,
        });
      });

      setTodos(sortTodos(todosData));
      setError(null);
    } catch (err: any) {
      console.error('Erro ao carregar tarefas');
      setError('Erro ao carregar tarefas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user?.uid) {
      setTodos([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    const q = query(
      collection(db, 'todos'),
      where('userId', '==', user.uid),
      orderBy('date', 'asc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const todosData: Todo[] = [];

        snapshot.forEach((docSnapshot) => {
          const data = docSnapshot.data();

          if (data.userId !== user.uid) {
            console.warn('Todo de outro usuário encontrado:', docSnapshot.id);
            return;
          }

          const todoDate = data.date?.toDate ? data.date.toDate() : new Date();

          todosData.push({
            id: docSnapshot.id,
            title: data.title || '',
            completed: data.completed || false,
            date: normalizeDate(todoDate),
            createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(),
            updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date(),
            userId: data.userId,
            eventId: data.eventId,
            eventTitle: data.eventTitle,
          });
        });

        setTodos(sortTodos(todosData));
        setError(null);
        setLoading(false);
      },
      (err) => {
        console.error('Erro ao escutar tarefas:', err);
        setError('Erro ao carregar tarefas');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user?.uid]);

  // Adicionar tarefa
  const addTodo = async (title: string, date: Date): Promise<void> => {
    if (!user?.uid) {
      throw new Error('Usuário não autenticado');
    }

    try {
      const normalizedDate = normalizeDate(date);
      const cleanedData = {
        title: title.trim(),
        completed: false,
        date: Timestamp.fromDate(normalizedDate),
        userId: user.uid,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };

      await addDoc(collection(db, 'todos'), cleanedData);
    } catch (err: any) {
      console.error('Erro ao adicionar tarefa:', err);
      throw new Error('Erro ao adicionar tarefa');
    }
  };

  // Alternar estado de conclusão
  const toggleTodo = async (todoId: string, completed: boolean): Promise<void> => {
    try {
      const todoRef = doc(db, 'todos', todoId);
      await updateDoc(todoRef, {
        completed,
        updatedAt: Timestamp.now(),
      });
    } catch (err: any) {
      console.error('Erro ao atualizar tarefa:', err);
      throw new Error('Erro ao atualizar tarefa');
    }
  };

  // Deletar tarefa
  const deleteTodo = async (todoId: string): Promise<void> => {
    try {
      await deleteDoc(doc(db, 'todos', todoId));
    } catch (err: any) {
      console.error('Erro ao deletar tarefa:', err);
      throw new Error('Erro ao deletar tarefa');
    }
  };

  // Atualizar tarefa
  const updateTodo = async (todoId: string, title: string, date: Date): Promise<void> => {
    try {
      const normalizedDate = normalizeDate(date);
      const todoRef = doc(db, 'todos', todoId);
      await updateDoc(todoRef, {
        title: title.trim(),
        date: Timestamp.fromDate(normalizedDate),
        updatedAt: Timestamp.now(),
      });
    } catch (err: any) {
      console.error('Erro ao atualizar tarefa:', err);
      throw new Error('Erro ao atualizar tarefa');
    }
  };

  return {
    todos,
    todosForDate,
    completedTodos,
    overdueTodos,
    loading,
    error,
    addTodo,
    toggleTodo,
    deleteTodo,
    updateTodo,
    refreshTodos: fetchTodos,
  };
};
